import { Badge, Group, Loader, Pagination, Stack, Switch, Text, Tooltip } from '@mantine/core';
import { IconExternalLink, IconInbox, IconSparkles } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import type { ZendeskDraftSummary } from '@mygpt/app/apis/services/zendesk';
import { unitStatusColor } from '@/components/Datasets/KnowledgeLoop/shared';
import {
  ACCENT,
  CARD_SHADOW,
  formatDateTime,
  formatSimilarity,
  isKnowledgeGap,
  reasonLabel,
  reviewColor,
  reviewDisplay,
  statusColor,
  statusLabel,
} from './shared';

export type QueueFilter = 'all' | 'edited' | 'self' | 'knowledge_gap' | 'posted' | 'as_is' | 'skipped' | 'dry_run';

// 筛选 chip → 后端 status / skip_reason 参数（逗号分隔）
export const FILTER_PARAMS: Record<QueueFilter, { status?: string; skip_reason?: string }> = {
  all: {},
  edited: { status: 'answered_edited' },
  self: { status: 'answered_self' },
  knowledge_gap: { status: 'skipped', skip_reason: 'knowledge_gap,unanswerable' },
  posted: { status: 'posted,inserted,generating' },
  as_is: { status: 'answered_as_is' },
  skipped: { status: 'skipped,failed,discarded' },
  dry_run: { status: 'dry_run' },
};

export const PAGE_SIZE = 20;

interface Props {
  items: ZendeskDraftSummary[];
  total: number;
  page: number;
  loading: boolean;
  filter: QueueFilter;
  counts: Partial<Record<QueueFilter, number>>;
  grouped: boolean;
  onFilter: (f: QueueFilter) => void;
  onGrouped: (v: boolean) => void;
  onPage: (p: number) => void;
  onOpen: (d: ZendeskDraftSummary) => void;
}

const COLS = {
  ticket: '0 0 96px',
  subject: '1 1 auto',
  verdict: '0 0 132px',
  similarity: '0 0 64px',
  reason: '0 0 130px',
  knowledge: '0 0 120px',
  review: '0 0 92px',
  assignee: '0 0 90px',
  updated: '0 0 104px',
};

export const Queue = ({
  items,
  total,
  page,
  loading,
  filter,
  counts,
  grouped,
  onFilter,
  onGrouped,
  onPage,
  onOpen,
}: Props) => {
  const { t } = useTranslation();
  // 試運転のチップは件数があるときだけ
  const filters: QueueFilter[] = (
    ['all', 'edited', 'self', 'knowledge_gap', 'posted', 'as_is', 'skipped', 'dry_run'] as QueueFilter[]
  ).filter((f) => f !== 'dry_run' || (counts.dry_run || 0) > 0 || filter === 'dry_run');

  // ナレッジ列：提案した知識の状態 > 改善結果 > —
  const knowledgeCell = (d: ZendeskDraftSummary) => {
    if (d.knowledge_unit_status) {
      return (
        <Badge
          variant="light"
          color={unitStatusColor(d.knowledge_unit_status)}
          size="sm"
          radius="sm"
          style={{ textTransform: 'none' }}
        >
          {t(`zendesk_ops.unit_status.${d.knowledge_unit_status}`)}
        </Badge>
      );
    }
    if (d.review_outcome) {
      return (
        <Text fz={12} c="gray.7" truncate>
          {t(`zendesk_ops.outcome.${d.review_outcome}`)}
        </Text>
      );
    }
    if (d.status === 'skipped' && isKnowledgeGap(d.skip_reason)) {
      return (
        <Text fz={12} c="orange.7" truncate>
          {t('zendesk_ops.knowledge_cell.gap')}
        </Text>
      );
    }
    return (
      <Text fz={12} c="gray.4">
        —
      </Text>
    );
  };

  return (
    <Stack gap={12}>
      <Group justify="space-between" align="center">
        <Group gap={8}>
          {filters.map((f) => {
            const active = f === filter;
            const n = counts[f];
            return (
              <button
                key={f}
                type="button"
                onClick={() => onFilter(f)}
                style={{
                  border: `1px solid ${active ? ACCENT : '#E3E6EB'}`,
                  background: active ? ACCENT : '#fff',
                  color: active ? '#fff' : '#495057',
                  borderRadius: 999,
                  padding: '5px 12px',
                  fontSize: 12.5,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                {t(`zendesk_ops.filter.${f}`)}
                {n !== undefined && (
                  <span
                    style={{
                      fontSize: 11,
                      padding: '0 6px',
                      borderRadius: 999,
                      background: active ? 'rgba(255,255,255,0.22)' : '#F1F3F5',
                      color: active ? '#fff' : '#868E96',
                    }}
                  >
                    {n}
                  </span>
                )}
              </button>
            );
          })}
        </Group>
        <Switch
          size="xs"
          checked={grouped}
          onChange={(e) => onGrouped(e.currentTarget.checked)}
          label={t('zendesk_ops.group.by_ticket')}
        />
      </Group>

      <div className="rounded-xl bg-white" style={{ boxShadow: CARD_SHADOW, overflow: 'hidden' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '11px 20px',
            borderBottom: '1px solid #ECECF1',
            background: 'linear-gradient(180deg, #FBFBFD, #F6F7FA)',
            fontSize: 11,
            fontWeight: 700,
            color: '#9AA0AC',
            letterSpacing: 0.4,
            textTransform: 'uppercase',
            gap: 12,
          }}
        >
          <div style={{ flex: COLS.ticket }}>{t('zendesk_ops.col.ticket')}</div>
          <div style={{ flex: COLS.subject, minWidth: 0 }}>{t('zendesk_ops.col.subject')}</div>
          <div style={{ flex: COLS.verdict }}>{t('zendesk_ops.col.verdict')}</div>
          <div style={{ flex: COLS.similarity }}>{t('zendesk_ops.col.similarity')}</div>
          <div style={{ flex: COLS.reason }}>{t('zendesk_ops.col.reason')}</div>
          <div style={{ flex: COLS.knowledge }}>{t('zendesk_ops.col.knowledge')}</div>
          <div style={{ flex: COLS.review }}>{t('zendesk_ops.col.review')}</div>
          <div style={{ flex: COLS.assignee }}>{t('zendesk_ops.col.assignee')}</div>
          <div style={{ flex: COLS.updated }}>{t('zendesk_ops.col.updated')}</div>
        </div>

        {loading && (
          <Group justify="center" py={40}>
            <Loader size="sm" />
          </Group>
        )}
        {!loading && items.length === 0 && (
          <Stack align="center" py={48} gap={8}>
            <IconInbox size={28} color="#C0C4CC" />
            <Text size="sm" c="gray.6">
              {t('zendesk_ops.empty')}
            </Text>
          </Stack>
        )}
        {!loading &&
          items.map((d, i) => {
            const rv = reviewDisplay(d);
            const reason = d.edit_reason || d.edit_reason_ai;
            const isAi = !d.edit_reason && !!d.edit_reason_ai;
            // チケット単位表示：同じチケットの古い版本は「↳ vN」で続ける
            const continued = grouped && i > 0 && items[i - 1].ticket_id === d.ticket_id;
            return (
              <div
                key={d.id}
                onClick={() => onOpen(d)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '13px 20px',
                  borderBottom: i < items.length - 1 ? '1px solid #F2F3F7' : 'none',
                  borderTop: continued ? '1px dashed #EEF0F4' : undefined,
                  marginTop: continued ? -1 : undefined,
                  background: continued ? '#FCFCFD' : undefined,
                  fontSize: 13,
                  cursor: 'pointer',
                  gap: 12,
                  transition: 'background 0.12s, box-shadow 0.12s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#FAFAFE';
                  e.currentTarget.style.boxShadow = `inset 3px 0 0 ${ACCENT}`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = continued ? '#FCFCFD' : '';
                  e.currentTarget.style.boxShadow = '';
                }}
              >
                <div style={{ flex: COLS.ticket, display: 'flex', alignItems: 'center', gap: 4 }}>
                  {continued ? (
                    <Text fz={12} c="gray.5" pl={18} style={{ fontFamily: 'monospace' }}>
                      ↳ v{d.draft_version}
                    </Text>
                  ) : (
                    <>
                      <Text fw={700} fz={13} style={{ fontFamily: 'monospace' }}>
                        #{d.ticket_id}
                      </Text>
                      {d.ticket_url && (
                        <a
                          href={d.ticket_url}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{ color: '#ADB5BD', display: 'flex' }}
                          title={t('zendesk_ops.drawer.open_zendesk')}
                        >
                          <IconExternalLink size={14} />
                        </a>
                      )}
                      {d.draft_version > 1 && (
                        <Text fz={10.5} c="gray.5">
                          v{d.draft_version}
                        </Text>
                      )}
                    </>
                  )}
                </div>
                <div style={{ flex: COLS.subject, minWidth: 0 }}>
                  <Text fz={13} c={continued ? 'gray.5' : d.ticket_subject ? 'gray.8' : 'gray.5'} truncate>
                    {d.ticket_subject || '—'}
                  </Text>
                </div>
                <div style={{ flex: COLS.verdict }}>
                  <Badge
                    variant="light"
                    color={statusColor(d.status)}
                    size="sm"
                    radius="sm"
                    style={{ textTransform: 'none' }}
                  >
                    {statusLabel(t, d.status, d.skip_reason)}
                  </Badge>
                </div>
                <div style={{ flex: COLS.similarity }}>
                  <Text fz={12.5} c="gray.7" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatSimilarity(d.similarity)}
                  </Text>
                </div>
                <div style={{ flex: COLS.reason, display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                  <Text fz={12.5} c={reason ? 'gray.7' : 'gray.4'} truncate>
                    {reason ? reasonLabel(t, reason) : '—'}
                  </Text>
                  {isAi && (
                    <Tooltip label={t('zendesk_ops.ai_chip_hint')} withArrow>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 2,
                          fontSize: 10,
                          fontWeight: 700,
                          color: '#7C68FB',
                          background: 'rgba(124,104,251,0.10)',
                          borderRadius: 999,
                          padding: '1px 6px',
                          flexShrink: 0,
                        }}
                      >
                        <IconSparkles size={10} />
                        {t('zendesk_ops.ai_chip')}
                      </span>
                    </Tooltip>
                  )}
                </div>
                <div style={{ flex: COLS.knowledge, minWidth: 0 }}>{knowledgeCell(d)}</div>
                <div style={{ flex: COLS.review }}>
                  <Badge variant="dot" color={reviewColor(rv)} size="sm" radius="sm" style={{ textTransform: 'none' }}>
                    {t(`zendesk_ops.review.${rv}`)}
                  </Badge>
                </div>
                <div style={{ flex: COLS.assignee, minWidth: 0 }}>
                  <Text fz={12.5} c="gray.7" truncate>
                    {d.reviewed_by || d.final_author_name || '—'}
                  </Text>
                </div>
                <div style={{ flex: COLS.updated }}>
                  <Text fz={12} c="gray.6" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatDateTime(d.updated_at)}
                  </Text>
                </div>
              </div>
            );
          })}
      </div>

      {total > PAGE_SIZE && (
        <Group justify="flex-end">
          <Pagination value={page} onChange={onPage} total={Math.ceil(total / PAGE_SIZE)} size="sm" />
        </Group>
      )}
    </Stack>
  );
};
